/**
 * Fluency decay: how a stored level ages when the reader stops touching it.
 *
 * Ported from `packages/ja-core/src/decay.ts` in `git@git.fugl.dev:sks/yominoki.git` @ `515ce23`.
 * Bindery is canonical for this module from 2026-09-04; reconcile yominoki-side drift toward this
 * copy when yominoki ports in.
 *
 * The thresholds live in `./decay-constants`, split out as they are in yominoki so the two trees
 * stay diffable file-for-file.
 *
 * **Decay is derived, never written.** Nothing here mutates a record. `fluencyRecords.level` keeps
 * whatever the reader last earned, and `decayedLevel` answers "what is that worth *now*" at read
 * time. A caller that persists the demoted value has turned a reversible view into a destructive
 * write, and the reader loses the level they earned.
 *
 * `now` is a parameter rather than a `Date.now()` closed over inside, so the whole module is a
 * pure function of its inputs and the tests can sit exactly either side of a threshold.
 *
 * The source declared `WordReadingFluencyEnum` as a TS `enum`; `erasableSyntaxOnly` forbids enums
 * repo-wide, so this imports `./fluency`'s `const`-object form. The member values are identical.
 */
import { WordReadingFluencyEnum } from "./fluency";
import { DECAY_DAYS, DEMOTE_TO } from "./decay-constants";

export interface DecayInput {
  level: WordReadingFluencyEnum;
  updatedAt: Date | number;
  now?: Date | number;
}

/**
 * Fractional days between two instants. Accepts either side as a `Date` or epoch milliseconds
 * so callers can pass a Convex `_creationTime` number or a Postgres-hydrated `Date` unchanged.
 */
export function daysSinceUpdate(
  updatedAt: Date | number,
  now: Date | number = Date.now(),
): number {
  const ms =
    (typeof now === "number" ? now : now.getTime()) -
    (typeof updatedAt === "number" ? updatedAt : updatedAt.getTime());
  return ms / 86_400_000;
}

/**
 * The level a record is worth right now, given how long it has sat untouched.
 *
 * The comparison is strictly `>`, so a record exactly at its threshold has not yet decayed;
 * `Strong` at exactly 60 days is still `Strong`. Demotion is one step — the result is not fed
 * back through, so a record 400 days stale lands on `Developing`, not on `Unknown`.
 */
export function decayedLevel(input: DecayInput): WordReadingFluencyEnum {
  const elapsed = daysSinceUpdate(input.updatedAt, input.now ?? Date.now());
  const threshold = DECAY_DAYS[input.level];
  if (elapsed > threshold) return DEMOTE_TO[input.level];
  return input.level;
}

/**
 * The level as a 0–100 percentage, for the `--level-pct` custom property that tints a tile.
 * Presentation only — nothing persists this number.
 */
export function levelPctForFluency(l: WordReadingFluencyEnum): number {
  switch (l) {
    case WordReadingFluencyEnum.Strong:
      return 100;
    case WordReadingFluencyEnum.Learned:
      return 75;
    case WordReadingFluencyEnum.Developing:
      return 50;
    case WordReadingFluencyEnum.Initial:
      return 25;
    case WordReadingFluencyEnum.Predicted:
      return 10;
    case WordReadingFluencyEnum.Unknown:
      return 0;
    // Defensive: TS exhaustiveness only protects in-process. The DB
    // returns INT, so a future fluency_records.level value not yet
    // mirrored into the union reaches this function. Render it as
    // unknown rather than emitting `--level-pct: undefined%`, which
    // browsers ignore and leaves the tile silently miscolored.
    default:
      return 0;
  }
}
